import Vue, { createApp } from 'vue';
import '@/ui/tokens/themes';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18n from '@/i18n';
import { checkWhetherMainWindow, lastTrackedTaskOfOpenWorkspace } from '@/store/utils';
import store from '@/store/';
import App from '@/App';
import router from '@/router';
import UserAvatar from '@/components/elements/UserAvatar';
import Dropdown from '@/components/elements/Dropdown';
import TaskLabelSelect from '@/components/widgets/TaskLabelSelect';
import EstimationProgressLine from '@/components/elements/EstimationProgressLine';
import Popup from '@/components/elements/Popup';
import appFileLogger from '@/utils/appFileLogger';
import { defineAsyncComponent } from 'vue'
import '@/assets/icon-vectors/file-icon-classic.min.css';

import '@/assets/svg_icons/tracklify-icons.css';
// import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import '@/assets/tailwind.css';
import { initProfiling } from './store/profiling';

window.isMainWindow = checkWhetherMainWindow();

initProfiling();


dayjs.extend(utc);

window.dayjs = dayjs;

(() => {
  const { userAgent, platform } = window.navigator;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  let os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'darwin';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'ios';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'win32';
  } else if (/Android/.test(userAgent)) {
    os = 'android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'linux';
  }
  window.devicePlatform = os;
  window.devicePlatformIsMobile = os === 'android' || os === 'ios';

  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  window.isSafariBrowser = isSafari;
  if (isSafari) {
    document.documentElement.classList.add('browser-safari');
  }
})();



if (process.env.VUE_APP_ENV !== 'live') {
  document.title += ` - ${process.env.VUE_APP_ENV}`;
}

// const permissionResult = await Notification.requestPermission();
// console.log('permissionResult', permissionResult);

if (window.isMainWindow && 'serviceWorker' in navigator) {
  let serviceWorkerExists = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('🐕‍🦺 Service worker controllerchange');
    if (!serviceWorkerExists) {
      serviceWorkerExists = true;
    } else {
      store.commit('user/mutationUpdateAvailable', 'patch');
    }
  });

  navigator.serviceWorker.addEventListener('message', async (e) => {
    console.log('🐕‍🦺 Service worker message', e);
    if (e.data.action === 'sharedData') {
      store.dispatch('user/actionSetSharedData', e.data);
    } else if (e.data.action === 'notificationResponse' && e.data.response === 'reject') {
      window.rejectedLastScreenshot = true;
    }
  });

  window.addEventListener('load', async () => {
    const swPath = process.env.VUE_APP_ENV === 'local' ? '/tracklify-sw.js' : '/service-worker.js';

    serviceWorkerExists = !!(await navigator.serviceWorker.getRegistrations()).length;
    const registration = await navigator.serviceWorker.register(swPath);
    console.log('🐕‍🦺 Service worker registered');

    window.activeSw = await window.navigator.serviceWorker.ready;

    setInterval(() => registration.update(), 10 * 60 * 1000);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        registration.update();
      }
    });
  });


  setInterval(() => { store.commit('system/setCurrentTime', dayjs().utc()); }, 1000);

}


// window.addEventListener('beforeinstallprompt', (e) => {
//   console.log('beforeinstallprompt', e);
//   store.dispatch('user/actionSetDeferredPrompt', e);
// });


const urlParams = new URLSearchParams(window.location.search);
const signinWith = urlParams.get('signinWith');
if (signinWith) {
  console.log('signinWith', signinWith);
  store.dispatch('user/initConnection', { setJwtToken: signinWith });
  window.history.replaceState({}, window.location.href.split('?')[0]);
}
/* eslint-disable */
const nondfWeb = process.env.VUE_APP_ENV === 'live' && !document.cookie?.includes('06854420-e2f6-11e9-83ec-49b05d749750');  //df id

if (nondfWeb) {
  console.log = () => { };
  try {
    (function (h, o, t, j, a, r) {
      h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments) };
      h._hjSettings = { hjid: 2548768, hjsv: 6 };
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script'); r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
  } catch (e) {
    console.error('Hotjar error', e)
  }
}
/* eslint-enable */

console.log('🤖 Am I Electron:', window.APP_TRON);


appFileLogger.init();

// Removed swipe gesture handling on mobile to avoid conflicts with Android system gestures

const app = createApp(App);
app.use(router);
app.use(store);
app.use(i18n);


app.config.globalProperties.utils = window.utils;

app.config.globalProperties.s3base = `https://static.tracklify.com/${process.env.VUE_APP_ENV}`;
window.s3base = app.config.globalProperties.s3base;  // fallback for old components not migrated to setup-components

// Register async components
app.component('Checkbox', defineAsyncComponent(() => import('@/components/elements/Checkbox')));
app.component('SimpleButton', defineAsyncComponent(() => import('@/components/elements/SimpleButton')));
app.component('Loader', defineAsyncComponent(() => import('@/components/elements/Loader')));
app.component('LoaderDocumentPreview', defineAsyncComponent(() => import('@/components/elements/LoaderDocumentPreview')));
app.component('InputGroup', defineAsyncComponent(() => import('@/components/elements/InputGroup')));
app.component('Time', defineAsyncComponent(() => import('@/components/elements/Time')));
app.component('Pager', defineAsyncComponent(() => import('@/components/elements/Pager')));
app.component('Comment', defineAsyncComponent(() => import('@/components/elements/Comment')));
app.component('Notification', defineAsyncComponent(() => import('@/components/elements/Notification')));
app.component('Checklist', defineAsyncComponent(() => import('@/components/elements/Checklist')));
app.component('TaskEvent', defineAsyncComponent(() => import('@/components/elements/TaskEvent')));
app.component('Screenshot', defineAsyncComponent(() => import('@/components/elements/Screenshot')));
app.component('Header', defineAsyncComponent(() => import('@/components/Header')));
app.component('TableTasks', defineAsyncComponent(() => import('@/components/elements/TableTasks')));

// components used on board should be loaded as non-lazy:
app.component('UserAvatar', UserAvatar);
app.component('Dropdown', Dropdown);
app.component('TaskLabelSelect', TaskLabelSelect);
app.component('EstimationProgressLine', EstimationProgressLine);

// popup might be used queckly when user opens task, no sense to lazyload
app.component('Popup', Popup);


// A bare <a href> without target="_blank" (e.g. comment HTML supplied by an MCP client) navigates
// the current window. In the browser that leaves Tracklify in the same tab; in the app Chromium fires
// beforeunload, which stops tracking, before the main process can cancel it in will-navigate.
// Stop the click and open external links outside. Tracklify links keep navigating in place.
document.addEventListener('click', (e) => {
  const anchor = e.target.closest('a[href]');
  if (!anchor || anchor.target === '_blank' || !/^https?:/.test(anchor.href)
    || anchor.origin === window.location.origin) {
    return;
  }
  e.preventDefault();
  if (window.APP_TRON) {
    window.APP_TRON.ipcRenderer.send('openExternal', anchor.href);
  } else {
    window.open(anchor.href, '_blank', 'noopener');
  }
});

if (window.APP_TRON) {
  const { ipcRenderer } = window.APP_TRON; // eslint-disable-line
  console.log('🤖 loaded electron :)');
  console.log('🪟 WINDOW:', document.location.hash);

  app.config.globalProperties.$electron = { ipcRenderer };

  ipcRenderer.on('log', (e, ...args) => console.log('⚛️', ...args));
  ipcRenderer.on('error', (e, ...args) => console.error('⚛️', ...args));
  ipcRenderer.on('dispatch', (e, ...args) => {
    const action = args.shift();
    store.dispatch(action, ...args, { root: true });
  });
  ipcRenderer.send('ipcReady', 'tasksWindow');
  // State.on('stateSaved', () => ipcRenderer.send('stateSaved'));
  ipcRenderer.send('setDefaultFileHandler', true);

  if (store.state.task.lastTrackedTask || lastTrackedTaskOfOpenWorkspace()) {
    ipcRenderer.send('activeStartLastTaskItem', true);
    ipcRenderer.send('activePauseLastTaskItem', false);
  }
}

setInterval(() => {
  if (performance.memory) {
    const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = performance.memory;
    console.log('🖥️ Memory usage (MB):', {
      used: (usedJSHeapSize / 1024 / 1024).toFixed(2),
      total: (totalJSHeapSize / 1024 / 1024).toFixed(2),
      limit: (jsHeapSizeLimit / 1024 / 1024).toFixed(2),
    });
  } else {
    console.log('🖥️ performance.memory is not supported in this browser');
  }
}, 5 * 60 * 1000);

app.mount('#app');
